import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import Modal, { IModalChildProps } from '../../../common/modal';
import { IOrderDetails } from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import { loadOrder } from '../../../../store/order/order-actions';
import { setIsBusy } from '../../../../store/api/api-status-action';
import {
    IShippingMethodOptions,
    IUpdateShippingMethod,
} from '../../../../models/shipping';
import { IITAPISResult } from '../../../../models/result-models';
import { changeShippingMethod } from '../../../../store/shipping/shipping-actions';
import { gaEvent } from '../../../../hooks/ga-hook';
interface IUpdatedShippingModal extends IModalChildProps {
    order: IOrderDetails;
    updatedShippingMethod: IShippingMethodOptions;
    loadOrder: (orderNumber: string) => Promise<void>;
    changeShippingMethod: (
        methodToSend: IUpdateShippingMethod
    ) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    updateShippingMethodResponse: IITAPISResult | null;
}

const UpdatedShippingModal = ({
    onAccept,
    onClose,
    order,
    updatedShippingMethod,
    loadOrder,
    setIsBusy,
    changeShippingMethod,
    updateShippingMethodResponse,
}: IUpdatedShippingModal) => {
    const intl = useIntl();
    const [success, setSuccess] = useState(false);
    const [methodToSend] = useState<IUpdateShippingMethod>({
        orderNumber: order.orderNumber,
        defaultShippingMethodCode:
            updatedShippingMethod.defaultShippingMethodCode,
    });
    const [error, setError] = useState(false);
    useEffect(() => {
        if (!updateShippingMethodResponse) return;

        if (updateShippingMethodResponse!.returnCodeType === 'Error') {
            setError(true);
        } else {
            setSuccess(true);
        }
    }, [updateShippingMethodResponse]);
    useEffect(() => {
        setError(false);
        setSuccess(false);
    }, []);
    const sendShippingChange = () => {
        gaEvent(
            'Estore_Order_Updates',
            'ShippingMethod_Modal_Update',
            order.billTo.accountNumber
        );
        setIsBusy(true);
        changeShippingMethod(methodToSend).then(() => {
            setIsBusy(false);
        });
    };

    return (
        <>
            {updatedShippingMethod && (
                <Modal>
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                id="closeModalConfirmShip"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({ id: 'Close' })}
                                onClick={onClose}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        {success && !error && (
                            <>
                                <p className="text-center">
                                    <span className="fa fa-check icon-green"></span>
                                    <strong>
                                        <FormattedMessage
                                            id="Shipping_method_successfully_changed"
                                            defaultMessage="Shipping method successfully changed"
                                        />
                                    </strong>
                                </p>
                            </>
                        )}
                        {!success && !error && (
                            <>
                                <p className="text-center">
                                    <FormattedMessage
                                        id="Are_you_sure_shipping_method"
                                        defaultMessage="Are you sure you want to change your
                                    shipping method?"
                                    />
                                    <br />
                                    <strong>
                                        {updatedShippingMethod.shippingMethod}
                                    </strong>
                                    <br />
                                    <FormattedMessage
                                        id="An_additional_charge_will_apply"
                                        defaultMessage="An additional charge will apply"
                                    />
                                </p>
                            </>
                        )}
                        {!success && error && (
                            <>
                                <p className="text-center">
                                    <strong>
                                        <FormattedMessage
                                            id="shipping_method_error"
                                            defaultMessage="We’re sorry there’s been an error
                                        changing your shipping method. Please
                                        try again"
                                        />
                                    </strong>
                                </p>
                            </>
                        )}
                        <div className="modal-footer">
                            {success && !error && (
                                <button
                                    onClick={onAccept}
                                    className="btn btn-info"
                                    id="closeModalButtonConfirmShip"
                                >
                                    <FormattedMessage
                                        id="Close"
                                        defaultMessage="Close"
                                    />
                                </button>
                            )}
                            {!success && error && (
                                <>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setError(false);
                                            setSuccess(false);
                                            onClose();
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setError(false);
                                            setSuccess(false);
                                            onClose();
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Retry"
                                            defaultMessage="Retry"
                                        />
                                    </button>
                                </>
                            )}
                            {!success && !error && (
                                <>
                                    <button
                                        type="button"
                                        id="cancelModalButtonConfirmShip"
                                        className="btn btn-default"
                                        onClick={onClose}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        onClick={sendShippingChange}
                                        id="confirmlModalButtonConfirmShip"
                                    >
                                        <FormattedMessage
                                            id="Save_Changes"
                                            defaultMessage="Save Changes"
                                        />
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                </Modal>
            )}
        </>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        updateShippingMethodResponse: state.updateShippingMethodResponse
            ? state.updateShippingMethodResponse.data!
            : null,
    };
};

const mapDispatchToProps = {
    loadOrder,
    setIsBusy,
    changeShippingMethod,
};

const UpdatedShippingModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(UpdatedShippingModal);

export default UpdatedShippingModalContainer;
